import React, { useCallback, useEffect, useRef, useState } from "react";
import { Tree, TreeNode } from "react-organizational-chart";
import { Download, Users, Network, Layers, Plus, Pencil, Trash2, X } from "lucide-react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { getEmployees, getOrgRoles, getStructureIdentity, getOrgGroups, createOrgGroup, updateOrgGroup, deleteOrgGroup } from "../../api";
import { useStructure } from "../../contexts/StructureContext";
import { useToast } from "../../contexts/NotificationContext";
import { Btn, Modal, FormField, inputCls } from "../ui";
import OrgChartCard from "./OrgChartCard";
import OrgChartSkeleton from "./OrgChartSkeleton";

const LINE_COLOR = "#14b8a680";

const TREE_PROPS = {
  lineWidth: "2px",
  lineColor: LINE_COLOR,
  lineBorderRadius: "12px",
  lineHeight: "44px",
  nodePadding: "10px",
};

// Construit l'arbre nœud par nœud — un employé = un nœud.
// Quand plusieurs employés ont le même rôle, les enfants sont distribués
// par structureId : chaque parent reçoit les enfants de sa propre structure.
// Les rôles sans employé sont transparents (leurs enfants remontent d'un niveau).
function buildNodeTree(employees, roles) {
  const empsByCode = {};
  for (const e of employees) {
    if (!e.role) continue;
    if (!empsByCode[e.role]) empsByCode[e.role] = [];
    empsByCode[e.role].push(e);
  }

  function nodesForRole(role) {
    const childRoles = roles
      .filter((r) => r.parentId === role.id)
      .sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));

    const allEmps = (empsByCode[role.code] || []).sort((a, b) =>
      a.lastName.localeCompare(b.lastName),
    );

    // Rôle vide → remonter les enfants directement
    if (allEmps.length === 0)
      return childRoles.flatMap((cr) => nodesForRole(cr));

    // Tous les nœuds enfants (tous rôles fils confondus)
    const allChildNodes = childRoles.flatMap((cr) => nodesForRole(cr));

    // Un seul employé → reçoit tous les enfants
    if (allEmps.length === 1) {
      return [
        {
          key: `${role.code}-${allEmps[0].id}`,
          emp: allEmps[0],
          childNodes: applyGroups(allChildNodes),
        },
      ];
    }

    // Plusieurs employés → chaque parent reçoit les enfants de sa structure.
    // Les employés avec une structure secondaire reçoivent un nœud fantôme
    // (isGhost) dans l'arbre de leur structure secondaire, relié par SVG.
    const parentStructures = new Set(allEmps.map((e) => e.structureId));
    const childrenByStructure = {};
    const orphans = [];

    for (const child of allChildNodes) {
      const primarySid = child.emp.structureId;

      // Nœud réel dans l'arbre principal
      if (parentStructures.has(primarySid)) {
        if (!childrenByStructure[primarySid])
          childrenByStructure[primarySid] = [];
        childrenByStructure[primarySid].push(child);
      } else {
        orphans.push(child);
      }

      // Nœud fantôme dans chaque arbre secondaire
      const secSids = (child.emp.secondaryStructures || [])
        .map((s) => s.structureId)
        .filter(
          (sid) => sid && parentStructures.has(sid) && sid !== primarySid,
        );

      for (const sid of secSids) {
        if (!childrenByStructure[sid]) childrenByStructure[sid] = [];
        childrenByStructure[sid].push({
          key: `ghost-${sid}-${child.emp.id}`,
          emp: child.emp,
          childNodes: [],
          isGhost: true,
        });
      }
    }

    return allEmps.map((emp, i) => ({
      key: `${role.code}-${emp.id}`,
      emp,
      childNodes: applyGroups([
        ...(childrenByStructure[emp.structureId] || []),
        ...(i === 0 ? orphans : []),
      ]),
    }));
  }

  const rootRoles = roles
    .filter((r) => !r.parentId)
    .sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));

  return rootRoles.flatMap((r) => nodesForRole(r));
}

// Regroupe les nœuds enfants par leur groupe org (orgGroup).
// Les nœuds sans groupe restent à plat ; les autres sont encapsulés dans un
// nœud synthétique { isGroup, group, childNodes }.
function applyGroups(childNodes) {
  const byGroup = {};
  const ungrouped = [];

  for (const node of childNodes) {
    const g = node.isGhost ? null : node.emp?.orgGroup;
    if (g) {
      if (!byGroup[g.id]) byGroup[g.id] = { group: g, nodes: [] };
      byGroup[g.id].nodes.push(node);
    } else {
      ungrouped.push(node);
    }
  }

  const groupNodes = Object.values(byGroup)
    .sort((a, b) => (a.group.position ?? 999) - (b.group.position ?? 999))
    .map(({ group, nodes }) => ({
      key: `group-${group.id}`,
      isGroup: true,
      group,
      childNodes: nodes,
    }));

  return [...ungrouped, ...groupNodes];
}

export const GROUP_COLORS = [
  { label: "Violet",  value: "#6366f1" },
  { label: "Rose",    value: "#ec4899" },
  { label: "Rouge",   value: "#ef4444" },
  { label: "Orange",  value: "#f97316" },
  { label: "Ambre",   value: "#f59e0b" },
  { label: "Vert",    value: "#22c55e" },
  { label: "Cyan",    value: "#06b6d4" },
  { label: "Bleu",    value: "#3b82f6" },
];

function GroupBubble({ group, children }) {
  return (
    <div
      className="rounded-2xl border-2 px-5 pt-3 pb-4 mx-auto shadow-sm"
      style={{
        backgroundColor: group.color + "0e",
        borderColor: group.color + "65",
      }}
    >
      <div className="flex items-center justify-center gap-2 mb-4">
        <span
          className="w-2 h-2 rounded-full flex-shrink-0"
          style={{ backgroundColor: group.color }}
        />
        <span
          className="text-[11px] font-bold uppercase tracking-widest"
          style={{ color: group.color }}
        >
          {group.name}
        </span>
      </div>
      <div className="flex flex-wrap gap-3 justify-center">
        {children}
      </div>
    </div>
  );
}

// Collecte les paires (parentId → enfant fantôme) pour tracer les traits SVG.
function collectGhostLinks(nodes) {
  const links = [];
  function walk(node) {
    if (node.isGroup) { node.childNodes.forEach(walk); return; }
    for (const child of node.childNodes) {
      if (child.isGhost) links.push({ parentId: node.emp.id, childId: child.emp.id });
      else walk(child);
    }
  }
  nodes.forEach(walk);
  return links;
}

function useSecondaryLines(containerRef, ghostLinks) {
  const [lines, setLines] = useState([]);
  const prevRef = useRef("");

  useEffect(() => {
    if (!containerRef.current || !ghostLinks.length) { setLines([]); return; }
    const container = containerRef.current;

    function compute() {
      const rect = container.getBoundingClientRect();
      const sl = container.scrollLeft;
      const st = container.scrollTop;
      const result = [];

      for (const { parentId, childId } of ghostLinks) {
        const pEl = container.querySelector(`[data-emp-id="${parentId}"]`);
        const cEl = container.querySelector(`[data-emp-id="${childId}"]`);
        if (!pEl || !cEl) continue;
        const pr = pEl.getBoundingClientRect();
        const cr = cEl.getBoundingClientRect();
        result.push({
          x1: pr.left - rect.left + sl + pr.width / 2,  // bas-centre du parent
          y1: pr.bottom - rect.top + st,
          x2: cr.left - rect.left + sl + cr.width / 2,  // haut-centre de l'enfant
          y2: cr.top - rect.top + st,
        });
      }

      const key = result.map((l) =>
        `${Math.round(l.x1)},${Math.round(l.y1)},${Math.round(l.x2)},${Math.round(l.y2)}`
      ).join("|");
      if (key !== prevRef.current) { prevRef.current = key; setLines(result); }
    }

    const t = setTimeout(compute, 50);
    const ro = new ResizeObserver(compute);
    ro.observe(container);
    return () => { clearTimeout(t); ro.disconnect(); };
  }, [ghostLinks]); // eslint-disable-line

  return lines;
}

// Les nœuds ghost sont ignorés dans le rendu — seul le trait SVG les relie.
function renderNodes(nodes, makeCard) {
  return nodes.flatMap((node) => {
    if (node.isGhost) return [];
    if (node.isGroup) {
      return [
        <TreeNode
          key={node.key}
          label={
            <GroupBubble group={node.group}>
              {node.childNodes.filter((c) => !c.isGhost).map((c) => makeCard(c.emp))}
            </GroupBubble>
          }
        />,
      ];
    }
    return [
      <TreeNode key={node.key} label={makeCard(node.emp)}>
        {renderNodes(node.childNodes, makeCard)}
      </TreeNode>,
    ];
  });
}

function SansRoleSection({ employees, makeCard }) {
  return (
    <div className="mt-8 w-full border-t border-dashed border-gray-200 pt-5">
      <p className="text-xs text-center text-gray-400 mb-3 italic">
        Sans rôle défini ({employees.length})
      </p>
      <div className="flex flex-wrap justify-center gap-3">
        {employees.map((emp) => makeCard(emp))}
      </div>
      <p className="text-[11px] text-center text-gray-300 mt-2">
        Renseignez les rôles via les fiches employés pour compléter
        l'organigramme.
      </p>
    </div>
  );
}


function StructureCard({ structId, name, allStructEmps, orgRoles, roleNameFor, mode, onEmployeeClick }) {
  const containerRef = useRef(null);
  const nodes = buildNodeTree(allStructEmps, orgRoles);
  const ghostLinks = collectGhostLinks(nodes);
  const secondaryLines = useSecondaryLines(containerRef, ghostLinks);

  const makeStructCard = (emp) => (
    <OrgChartCard
      key={emp.id}
      employee={emp}
      roleName={roleNameFor(emp.role)}
      mode={mode}
      onClick={onEmployeeClick ? () => onEmployeeClick(emp) : undefined}
      isSecondary={emp.structureId !== structId}
    />
  );

  return (
    <div className="rounded-2xl border border-gray-200 overflow-hidden shadow-sm bg-white">
      <div
        className="flex items-center gap-3 px-5 py-3.5 border-b border-gray-100"
        style={{ background: 'linear-gradient(to right, rgb(var(--color-primary) / 0.07), transparent)' }}
      >
        <span className="w-2.5 h-2.5 rounded-full bg-primary flex-shrink-0" />
        <span className="font-bold text-gray-800 text-sm flex-1 tracking-tight">
          {name || "Structure"}
        </span>
        <span className="flex items-center gap-1.5 text-xs text-gray-500 bg-white border border-gray-200 rounded-full px-2.5 py-0.5 shadow-sm">
          <Users size={10} className="text-primary" />
          {allStructEmps.length}
        </span>
      </div>
      <div ref={containerRef} className="relative overflow-x-auto px-6 py-6">
        {secondaryLines.length > 0 && (
          <svg
            className="absolute inset-0 pointer-events-none"
            style={{
              width: containerRef.current?.scrollWidth || "100%",
              height: containerRef.current?.scrollHeight || "100%",
              zIndex: 0,
            }}
          >
            {secondaryLines.map((l, i) => {
              const my = (l.y1 + l.y2) / 2;
              return (
                <path
                  key={i}
                  d={`M ${l.x1} ${l.y1} V ${my} H ${l.x2} V ${l.y2}`}
                  stroke="#14b8a6"
                  strokeWidth="2"
                  strokeDasharray="6 3"
                  fill="none"
                  opacity="0.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              );
            })}
          </svg>
        )}
        <div style={{ minWidth: "max-content" }}>
          {nodes.length === 0 ? null : nodes.length === 1 ? (
            <Tree {...TREE_PROPS} label={makeStructCard(nodes[0].emp)}>
              {renderNodes(nodes[0].childNodes, makeStructCard)}
            </Tree>
          ) : (
            <div className="flex justify-start items-start gap-8">
              {nodes.map((n) => (
                <Tree key={n.key} {...TREE_PROPS} label={makeStructCard(n.emp)}>
                  {renderNodes(n.childNodes, makeStructCard)}
                </Tree>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function OrgTree({
  employees,
  orgRoles,
  onEmployeeClick,
  mode,
  currentStructureId,
}) {
  const knownCodes = new Set(orgRoles.map((r) => r.code));
  const withRole = employees.filter((e) => e.role && knownCodes.has(e.role));
  const sansRole = employees.filter((e) => !e.role || !knownCodes.has(e.role));

  const roleNameFor = (code) =>
    orgRoles.find((r) => r.code === code)?.name || code || "";
  const isSecondary = (emp) =>
    !!(currentStructureId && emp.structureId !== currentStructureId);

  const makeCard = (emp) => (
    <OrgChartCard
      key={emp.id}
      employee={emp}
      roleName={roleNameFor(emp.role)}
      mode={mode}
      onClick={onEmployeeClick ? () => onEmployeeClick(emp) : undefined}
      isSecondary={isSecondary(emp)}
    />
  );

  // Hooks must be declared before any conditional returns
  const containerRef = useRef(null);
  const allNodes = currentStructureId ? buildNodeTree(withRole, orgRoles) : [];
  const ghostLinks = currentStructureId ? collectGhostLinks(allNodes) : [];
  const secondaryLines = useSecondaryLines(containerRef, ghostLinks);


  // ── Mode toutes les structures : un arbre indépendant par structure ──
  if (!currentStructureId) {
    const byStruct = {};
    for (const emp of withRole) {
      if (!byStruct[emp.structureId])
        byStruct[emp.structureId] = { name: emp.structure?.name, emps: [] };
      byStruct[emp.structureId].emps.push(emp);
    }
    const structList = Object.entries(byStruct);

    return (
      <div className="flex flex-col pb-4 org-chart-capture w-full space-y-6">
        {structList.map(([structId, { name, emps }]) => {
          const secondaryEmps = withRole.filter(
            (emp) =>
              emp.structureId !== structId &&
              (emp.secondaryStructures || []).some((s) => s.structureId === structId),
          );
          return (
            <StructureCard
              key={structId}
              structId={structId}
              name={name}
              allStructEmps={[...emps, ...secondaryEmps]}
              orgRoles={orgRoles}
              roleNameFor={roleNameFor}
              mode={mode}
              onEmployeeClick={onEmployeeClick}
            />
          );
        })}
      </div>
    );
  }

  // ── Mode structure unique ────────────────────────────────────────────
  const secondaryStandalones = allNodes.filter(
    (n) => n.childNodes.length === 0 && isSecondary(n.emp),
  );
  const mainNodes = allNodes.filter(
    (n) => n.childNodes.length > 0 || !isSecondary(n.emp),
  );

  return (
    <div
      ref={containerRef}
      className={`relative flex flex-col pb-4 org-chart-capture ${mode === "pdf" ? "items-center" : "items-start"}`}
      style={mode === "pdf" ? { width: "max-content" } : { minWidth: "max-content" }}
    >
      {secondaryLines.length > 0 && (
        <svg
          className="absolute inset-0 pointer-events-none"
          style={{
            width: containerRef.current?.scrollWidth || "100%",
            height: containerRef.current?.scrollHeight || "100%",
            zIndex: 0,
          }}
        >
          {secondaryLines.map((l, i) => {
            const my = (l.y1 + l.y2) / 2;
            return (
              <path
                key={i}
                d={`M ${l.x1} ${l.y1} V ${my} H ${l.x2} V ${l.y2}`}
                stroke="#14b8a6"
                strokeWidth="2"
                strokeDasharray="6 3"
                fill="none"
                opacity="0.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            );
          })}
        </svg>
      )}
      {/* ── Arbre principal ─────────────────────────────────────────── */}
      {mainNodes.length === 0 ? null : mainNodes.length === 1 ? (
        <Tree {...TREE_PROPS} label={makeCard(mainNodes[0].emp)}>
          {renderNodes(mainNodes[0].childNodes, makeCard)}
        </Tree>
      ) : (
        // Plusieurs racines dans la même structure (rare)
        <div style={{ minWidth: "max-content" }}>
          <div className="flex justify-start items-start px-4 py-2">
            {mainNodes.map((node) => (
              <div key={node.key} className="flex-shrink-0 flex flex-col items-center gap-3">
                <Tree {...TREE_PROPS} label={makeCard(node.emp)}>
                  {renderNodes(node.childNodes, makeCard)}
                </Tree>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Rattachements secondaires ──────────────────────────────── */}
      {secondaryStandalones.length > 0 && (
        <div className="mt-8 w-full">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs font-medium text-gray-400 uppercase tracking-wide whitespace-nowrap">
              Rattachements secondaires
            </span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>
          <div className="flex flex-wrap gap-4 justify-center">
            {secondaryStandalones.map((node) => makeCard(node.emp))}
          </div>
        </div>
      )}

      {/* ── Sans rôle ──────────────────────────────────────────────── */}
      {sansRole.length > 0 && (
        <SansRoleSection employees={sansRole} makeCard={makeCard} />
      )}

    </div>
  );
}

function findInTree(nodes, id) {
  for (const n of nodes) {
    if (n.isGroup) {
      if (findInTree(n.childNodes, id)) return true;
    } else {
      if (n.emp.id === id) return true;
      if (findInTree(n.childNodes, id)) return true;
    }
  }
  return false;
}

export default function OrgChartTab({
  showAllStructures = false,
  onEmployeeClick,
}) {
  const { currentStructure } = useStructure();
  const toast = useToast();
  const treeRef = useRef(null);

  const [employees, setEmployees] = useState([]);
  const [orgRoles, setOrgRoles] = useState([]);
  const [orgGroups, setOrgGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [pdfMode, setPdfMode] = useState(false);
  const [showGroupManager, setShowGroupManager] = useState(false);

  const structureId = showAllStructures ? "all" : currentStructure?.id;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status: "active" };
      if (structureId && structureId !== "all")
        params.structureId = structureId;
      const [empRes, rolesRes, groupsRes] = await Promise.all([
        getEmployees(params),
        getOrgRoles(),
        getOrgGroups(),
      ]);
      setEmployees(empRes.data);
      setOrgRoles(rolesRes.data);
      setOrgGroups(groupsRes.data);
    } catch {
      toast.error("Erreur chargement organigramme");
    }
    setLoading(false);
  }, [structureId]); // eslint-disable-line

  useEffect(() => {
    load();
  }, [load]);

  // Exclure les employés dont la date de début est dans le futur (déjà créés
  // mais arrivée à venir — ex: contrat signé pour le mois prochain).
  const todayISO = new Date().toISOString().slice(0, 10);
  const filtered = employees.filter(
    (e) => !e.startDate || e.startDate.slice(0, 10) <= todayISO,
  );

  const knownCodes = new Set(orgRoles.map((r) => r.code));
  const withRole = filtered.filter(
    (e) => e.role && knownCodes.has(e.role),
  ).length;

  const handleExportPdf = async () => {
    if (!treeRef.current) return;
    setExporting(true);
    setPdfMode(true);

    try {
      // 1. Charger l'identité de la structure (logo + nom complet)
      let identity = null;
      if (!showAllStructures && currentStructure?.id) {
        try {
          identity = (await getStructureIdentity(currentStructure.id)).data;
        } catch {
          /* sans logo */
        }
      }

      // 2. Charger le logo en dataURL si disponible + détecter son ratio natif
      let logoDataUrl = null;
      let logoAspect = 1; // largeur / hauteur
      if (identity?.logoUrl) {
        try {
          // En local, REACT_APP_API_URL pointe sur le backend (port 3001).
          // En DEV/PROD, nginx proxy /uploads/ → backend, donc le chemin relatif suffit.
          const backendOrigin = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '');
          const logoFullUrl = identity.logoUrl.startsWith('http')
            ? identity.logoUrl
            : `${backendOrigin}${identity.logoUrl}`;
          const resp = await fetch(logoFullUrl);
          const blob = await resp.blob();
          let dataUrl = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
          // jsPDF ne supporte pas les SVG — conversion en PNG via canvas haute résolution
          if (dataUrl && (blob.type.includes('svg') || dataUrl.includes('svg+xml'))) {
            const result = await new Promise((resolve) => {
              // Récupérer le ratio depuis viewBox ou width/height
              let svgAspect = 1;
              let svgSrc = dataUrl;
              try {
                const raw = dataUrl.startsWith('data:image/svg+xml;base64,')
                  ? atob(dataUrl.split(',')[1])
                  : decodeURIComponent(dataUrl.split(',')[1]);
                const vbMatch = raw.match(/viewBox="([^"]+)"/);
                if (vbMatch) {
                  const [, , vw, vh] = vbMatch[1].split(/\s+/).map(Number);
                  if (vw > 0 && vh > 0) svgAspect = vw / vh;
                } else {
                  const wM = raw.match(/\swidth="([\d.]+)/);
                  const hM = raw.match(/\sheight="([\d.]+)/);
                  if (wM && hM) {
                    const wv = Number(wM[1]); const hv = Number(hM[1]);
                    if (wv > 0 && hv > 0) svgAspect = wv / hv;
                  }
                }
                // Reconstruire le SVG en respectant le ratio : 1024 sur le plus grand côté
                const target = 1024;
                const renderW = svgAspect >= 1 ? target : Math.round(target * svgAspect);
                const renderH = svgAspect >= 1 ? Math.round(target / svgAspect) : target;
                const sized = raw.replace(/<svg([^>]*)>/, (m, attrs) => {
                  const cleaned = attrs.replace(/\swidth="[^"]*"/g, '').replace(/\sheight="[^"]*"/g, '');
                  return `<svg${cleaned} width="${renderW}" height="${renderH}">`;
                });
                svgSrc = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(sized);

                const img = new Image();
                img.onload = () => {
                  const c = document.createElement('canvas');
                  c.width = renderW; c.height = renderH;
                  c.getContext('2d').drawImage(img, 0, 0, renderW, renderH);
                  resolve({ url: c.toDataURL('image/png'), aspect: svgAspect });
                };
                img.onerror = () => resolve(null);
                img.src = svgSrc;
              } catch {
                resolve(null);
              }
            });
            if (result) { dataUrl = result.url; logoAspect = result.aspect; }
          } else if (dataUrl) {
            // Raster (PNG/JPG) : récupérer le ratio naturel
            logoAspect = await new Promise((resolve) => {
              const probe = new Image();
              probe.onload = () => resolve(probe.naturalWidth / probe.naturalHeight);
              probe.onerror = () => resolve(1);
              probe.src = dataUrl;
            });
          }
          logoDataUrl = dataUrl;
        } catch {
          /* sans logo */
        }
      }

      // 3. Attendre le re-render en mode PDF puis capturer uniquement le contenu de l'arbre
      await new Promise((r) => setTimeout(r, 200));
      const captureEl =
        treeRef.current.querySelector(".org-chart-capture") || treeRef.current;
      const canvas = await html2canvas(captureEl, {
        scale: 3,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
      });

      // 4. Construire le PDF
      const pdf = new jsPDF("l", "mm", "a4");
      const pdfW = pdf.internal.pageSize.getWidth(); // 297mm
      const pdfH = pdf.internal.pageSize.getHeight(); // 210mm
      const M = 3; // marges 3mm

      const structureName = showAllStructures
        ? "Toutes les structures"
        : identity?.fullName || currentStructure?.name || "";
      const today = new Date().toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });

      // ── En-tête ─────────────────────────────────────────────
      const headerH = 26;

      // Bande de couleur en haut
      pdf.setFillColor(20, 184, 166);
      pdf.rect(0, 0, pdfW, headerH, "F");

      const textStartX = M + 4;

      // Titre "ORGANIGRAMME"
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(15);
      pdf.setTextColor(255, 255, 255);
      pdf.text("ORGANIGRAMME", pdfW / 2, headerH / 2 - 1, {
        align: "center",
        baseline: "middle",
      });

      // Nom de la structure + adresse (sous le titre, centré)
      if (structureName) {
        const addressParts = [
          identity?.structureAddress,
          identity?.structurePostalCode && identity?.structureCity
            ? `${identity.structurePostalCode} ${identity.structureCity}`
            : identity?.structureCity || null,
        ].filter(Boolean);
        const structureLine = addressParts.length
          ? `${structureName} — ${addressParts.join(", ")}`
          : structureName;
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(9);
        pdf.setTextColor(220, 255, 250);
        pdf.text(structureLine, pdfW / 2, headerH / 2 + 7, {
          align: "center",
          baseline: "middle",
        });
      }

      // Date (à droite)
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8);
      pdf.setTextColor(220, 255, 250);
      pdf.text(today, pdfW - M, headerH / 2, {
        align: "right",
        baseline: "middle",
      });

      // ── Zone arbre ───────────────────────────────────────────
      const footerH = 6;
      const treeY = headerH + 2;
      const treeMaxH = pdfH - treeY - footerH - 1;
      const treeMaxW = pdfW - M * 2;

      const ratio = Math.min(treeMaxW / canvas.width, treeMaxH / canvas.height);
      const imgW = canvas.width * ratio;
      const imgH = canvas.height * ratio;
      const imgX = (pdfW - imgW) / 2;
      const imgY = treeY + (treeMaxH - imgH) / 2;

      pdf.addImage(
        canvas.toDataURL("image/png"),
        "PNG",
        imgX,
        imgY,
        imgW,
        imgH,
      );

      // Logo dans le bandeau vert, haut-gauche, dans une carte blanche arrondie
      // pour bien le détacher du fond teal
      if (logoDataUrl) {
        const cardPaddingY = 2; // espace haut/bas dans le bandeau
        const cardH = headerH - cardPaddingY * 2;
        const innerPad = 2; // padding interne entre la carte et le logo
        const logoMaxH = cardH - innerPad * 2;
        const logoMaxW = 38; // largeur max raisonnable dans le bandeau
        // "Contain" du logo dans la box (logoMaxW × logoMaxH)
        let logoH = logoMaxH;
        let logoW = logoH * logoAspect;
        if (logoW > logoMaxW) {
          logoW = logoMaxW;
          logoH = logoW / logoAspect;
        }
        const cardW = logoW + innerPad * 2;
        const cardX = M + 2;
        const cardY = cardPaddingY;
        const logoX = cardX + innerPad;
        const logoY = cardY + (cardH - logoH) / 2;
        try {
          pdf.setFillColor(255, 255, 255);
          pdf.roundedRect(cardX, cardY, cardW, cardH, 2, 2, "F");
          pdf.addImage(logoDataUrl, "PNG", logoX, logoY, logoW, logoH);
        } catch { /* sans logo */ }
      }

      // ── Pied de page ─────────────────────────────────────────
      pdf.setFillColor(248, 248, 248);
      pdf.rect(0, pdfH - footerH, pdfW, footerH, "F");
      pdf.setDrawColor(220, 220, 220);
      pdf.setLineWidth(0.3);
      pdf.line(0, pdfH - footerH, pdfW, pdfH - footerH);

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(7.5);
      pdf.setTextColor(80, 80, 80);
      pdf.text("SuiviPlus", M, pdfH - footerH / 2, { baseline: "middle" });

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(7);
      pdf.setTextColor(150, 150, 150);
      pdf.text(
        "Document confidentiel — Usage interne",
        pdfW / 2,
        pdfH - footerH / 2,
        { align: "center", baseline: "middle" },
      );

      // ── Sauvegarde ───────────────────────────────────────────
      const slug = (structureName || "rh")
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/gi, "")
        .toLowerCase();
      pdf.save(
        `organigramme-${slug}-${new Date().toISOString().slice(0, 10)}.pdf`,
      );
      toast.success("PDF généré avec succès");
    } catch (e) {
      console.error(e);
      toast.error("Erreur génération PDF");
    }

    setPdfMode(false);
    setExporting(false);
  };

  if (loading) return <OrgChartSkeleton />;

  const cardMode = pdfMode ? "pdf" : "screen";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-center justify-end bg-white rounded-xl border border-gray-200 px-4 py-3">
        <span className="flex items-center gap-1 text-xs text-gray-400 whitespace-nowrap mr-auto">
          <Users size={13} />
          {withRole} avec rôle / {filtered.length}
        </span>
        <Btn
          variant="secondary"
          size="sm"
          onClick={() => setShowGroupManager(true)}
        >
          <Layers size={13} className="mr-1" /> Groupes
          {orgGroups.length > 0 && (
            <span className="ml-1 bg-primary/20 text-primary text-[10px] font-bold rounded-full px-1.5">
              {orgGroups.length}
            </span>
          )}
        </Btn>
        <Btn
          variant="secondary"
          size="sm"
          onClick={handleExportPdf}
          loading={exporting}
        >
          <Download size={13} className="mr-1" /> PDF
        </Btn>
      </div>

      <div
        ref={treeRef}
        className="bg-white rounded-xl border border-gray-200 p-8 min-h-[300px] overflow-x-auto"
      >
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <Users size={36} className="mb-3 opacity-20" />
            <p className="text-sm">Aucun employé actif trouvé.</p>
          </div>
        ) : withRole === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <Network size={36} className="mb-3 opacity-20" />
            <p className="text-sm font-medium mb-1">
              Aucun rôle hiérarchique défini
            </p>
            <p className="text-xs text-center max-w-xs">
              Renseignez les rôles via les fiches employés pour afficher
              l'organigramme.
            </p>
          </div>
        ) : (
          <div className="flex justify-start">
            <OrgTree
              employees={filtered}
              orgRoles={orgRoles}
              onEmployeeClick={onEmployeeClick}
              mode={cardMode}
              currentStructureId={
                structureId !== "all" ? structureId : undefined
              }
            />
          </div>
        )}
      </div>

      {showGroupManager && (
        <OrgGroupManager
          groups={orgGroups}
          onClose={() => setShowGroupManager(false)}
          onRefresh={load}
        />
      )}
    </div>
  );
}

// ── Gestionnaire de groupes (modal) ──────────────────────────────────────────

function OrgGroupManager({ groups, onClose, onRefresh }) {
  const toast = useToast();
  const [editingGroup, setEditingGroup] = useState(null);
  const [form, setForm] = useState({ name: "", color: GROUP_COLORS[0].value, position: 0 });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const openNew = () => {
    setEditingGroup({ id: null });
    setForm({ name: "", color: GROUP_COLORS[0].value, position: groups.length });
  };

  const openEdit = (g) => {
    setEditingGroup(g);
    setForm({ name: g.name, color: g.color, position: g.position });
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Le nom est obligatoire"); return; }
    setSaving(true);
    try {
      if (editingGroup.id) {
        await updateOrgGroup(editingGroup.id, form);
        toast.success("Groupe modifié");
      } else {
        await createOrgGroup(form);
        toast.success("Groupe créé");
      }
      setEditingGroup(null);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.error || "Erreur enregistrement");
    }
    setSaving(false);
  };

  const handleDelete = async (g) => {
    try {
      await deleteOrgGroup(g.id);
      toast.success("Groupe supprimé");
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.error || "Erreur suppression");
    }
    setDeleteConfirm(null);
  };

  return (
    <Modal
      title="Gérer les groupes"
      size="md"
      onClose={onClose}
      footer={
        <Btn size="sm" variant="secondary" onClick={onClose}>Fermer</Btn>
      }
    >
      <div className="space-y-4">
        <p className="text-xs text-gray-500">
          Les groupes permettent de réunir visuellement des employés au même niveau hiérarchique sous un bandeau coloré (ex : "Équipe crèche"). Assignez un groupe depuis la fiche employé.
        </p>

        <div className="space-y-2">
          {groups.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">Aucun groupe créé.</p>
          )}
          {groups.map((g) => (
            <div key={g.id} className="flex items-center gap-3 p-2.5 rounded-lg border border-gray-100 bg-gray-50">
              <span className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: g.color }} />
              <span className="flex-1 text-sm font-medium text-gray-800">{g.name}</span>
              <span className="text-xs text-gray-400">{g._count?.employees ?? 0} employé(s)</span>
              <Btn variant="ghost" size="sm" onClick={() => openEdit(g)}>
                <Pencil size={12} />
              </Btn>
              <Btn variant="ghost" size="sm" onClick={() => setDeleteConfirm(g)} className="text-red-400 hover:text-red-600">
                <Trash2 size={12} />
              </Btn>
            </div>
          ))}
        </div>

        <Btn size="sm" onClick={openNew}>
          <Plus size={13} className="mr-1" /> Nouveau groupe
        </Btn>

        {editingGroup && (
          <div className="border-t border-gray-100 pt-4 space-y-3">
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              {editingGroup.id ? "Modifier" : "Nouveau groupe"}
            </p>
            <FormField label="Nom" required>
              <input
                className={inputCls}
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Ex : Équipe crèche, Administration…"
                autoFocus
              />
            </FormField>
            <FormField label="Couleur">
              <div className="flex gap-2 flex-wrap">
                {GROUP_COLORS.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    title={c.label}
                    onClick={() => setForm((f) => ({ ...f, color: c.value }))}
                    className="w-7 h-7 rounded-full border-2 transition-all"
                    style={{
                      backgroundColor: c.value,
                      borderColor: form.color === c.value ? "#1f2937" : "transparent",
                      transform: form.color === c.value ? "scale(1.2)" : "scale(1)",
                    }}
                  />
                ))}
              </div>
            </FormField>
            <div className="flex gap-2 justify-end">
              <Btn variant="secondary" size="sm" onClick={() => setEditingGroup(null)}>Annuler</Btn>
              <Btn size="sm" loading={saving} onClick={handleSave}>
                {editingGroup.id ? "Enregistrer" : "Créer"}
              </Btn>
            </div>
          </div>
        )}
      </div>

      {deleteConfirm && (
        <Modal
          title="Supprimer le groupe"
          size="sm"
          onClose={() => setDeleteConfirm(null)}
          footer={
            <>
              <Btn variant="secondary" size="sm" onClick={() => setDeleteConfirm(null)}>Annuler</Btn>
              <Btn variant="danger" size="sm" onClick={() => handleDelete(deleteConfirm)}>Supprimer</Btn>
            </>
          }
        >
          <p className="text-sm text-gray-700">
            Supprimer <strong>{deleteConfirm.name}</strong> ? Les employés de ce groupe ne seront plus regroupés mais resteront dans l'organigramme.
          </p>
        </Modal>
      )}
    </Modal>
  );
}
